import { FC } from 'react';
import { Select as AntSelect } from 'antd';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { languagesMap } from '@/shared/dictionaries/languages';
import { LanguageCode } from '@/shared/types';

type ChooseLanguageV2Props = {
  options: LanguageCode[];
  value?: string;
  onChange?: (langKey: string) => void;
  className?: string;
  showOfficial?: boolean;
};

export const ChooseLanguageV2: FC<ChooseLanguageV2Props> = ({
  onChange,
  value,
  options,
  showOfficial = true,
  ...props
}) => {
  const enLanguage = languagesMap.get('EN');
  return (
    <Select
      dropdownAlign={{
        offset: [0, 20],
        points: ['tr', 'br'],
      }}
      popupMatchSelectWidth={false}
      value={value ? value.toUpperCase() : undefined}
      defaultValue="ES"
      dropdownStyle={{
        padding: 16,
        borderRadius: 12,
        minWidth: 216,
      }}
      labelRender={(v) => {
        return (
          <OptionContent key={v.value}>
            {languagesMap.get(String(v.value))?.icon ?? null}
            <span>{v.value}</span>
          </OptionContent>
        );
      }}
      onChange={(v) => {
        onChange?.(String(v));
      }}
      {...props}
    >
      {showOfficial && (
        <>
          <Select.Option
            key="official"
            style={{ padding: '0 0 4px 0', minHeight: 'unset' }}
            disabled
          >
            <TranslationMessage>Official version</TranslationMessage>
          </Select.Option>

          <Select.Option value="EN">
            <OptionContent>
              {enLanguage?.icon} {enLanguage?.i18nEn}
            </OptionContent>
          </Select.Option>
        </>
      )}

      <Select.Option key={'ai'} style={{ padding: '0 0 4px 0', minHeight: 'unset' }} disabled>
        <TranslationMessageBorder $showOfficial={showOfficial}>
          Translation generated by AI
        </TranslationMessageBorder>
      </Select.Option>

      {options.map((code) => {
        const codeUppercase = code.toUpperCase();
        if (codeUppercase === 'EN') return null;
        return (
          <Select.Option value={codeUppercase} key={codeUppercase}>
            <OptionContent>
              {languagesMap.get(codeUppercase)?.icon} {languagesMap.get(codeUppercase)?.i18nEn}
            </OptionContent>
          </Select.Option>
        );
      })}
    </Select>
  );
};

const OptionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled(AntSelect)`
  &&& .ant-select-selector {
    background: none;
  }
`;

const TranslationMessage = styled.div`
  padding: 0;
  opacity: 1;
  color: ${COLORS.systemDark200};
  padding-top: 0;
  font-size: 12px;
`;

const TranslationMessageBorder = styled(TranslationMessage)<{ $showOfficial: boolean }>`
  border-top: ${({ $showOfficial }) =>
    $showOfficial ? `1px solid ${COLORS.systemDark100}` : 'none'};
  padding-top: ${({ $showOfficial }) => ($showOfficial ? '12px' : '0')};
  margin-top: ${({ $showOfficial }) => ($showOfficial ? '12px' : '0')};
`;
